import React, { useState, useEffect, useMemo } from 'react';
import { formStyles } from '../../styles/formStyles';
import { tableStyles } from '../../styles/tableStyles';
import { API_URL } from '../../utils/constants';
import { formatDate, formatMoney } from '../../utils/constants';
import CustomerManagementForm from './CustomerManagementForm';

function DBManagement({ token }) {
  const [activeTab, setActiveTab] = useState('records'); // 'records' or 'customers'
  const [records, setRecords] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recordSearchTerm, setRecordSearchTerm] = useState('');
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [isRestoring, setIsRestoring] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [isLoadingSQL, setIsLoadingSQL] = useState(false);

  const fetchRecords = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/records`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        const sortedData = data.sort((a, b) => new Date(b.receipt_date) - new Date(a.receipt_date));
        setRecords(sortedData);
      }
    } catch (err) {
      console.error('데이터 로드 오류:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await fetch(`${API_URL}/customers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setCustomers(data);
      }
    } catch (err) {
      console.error('고객 데이터 로드 오류:', err);
    }
  };

  useEffect(() => {
    if (token) {
      fetchRecords();
      fetchCustomers();
    }
  }, [token]);

  // 작업 데이터 필터링
  const filteredRecords = useMemo(() => {
    const term = recordSearchTerm.trim().toLowerCase();
    if (!term) return records.slice(0, 60);

    const onlyNumbers = term.replace(/[^0-9]/g, '');
    const isNumeric = onlyNumbers.length > 0;
    if (isNumeric && term.length < 4) return [];
    if (!isNumeric && term.length < 2) return [];

    return records.filter((record) => {
      const company = (record.company_name || '').toLowerCase();
      const manager = (record.manager || '').toLowerCase();
      const phone = (record.phone || '').toLowerCase();
      const mobile = (record.mobile || '').toLowerCase();
      const details = (record.receipt_details || '').toLowerCase();
      
      return company.includes(term) || 
             manager.includes(term) || 
             phone.includes(term) || 
             mobile.includes(term) ||
             details.includes(term);
    });
  }, [records, recordSearchTerm]);

  // 고객 데이터 필터링
  const filteredCustomers = useMemo(() => {
    const term = customerSearchTerm.trim().toLowerCase();
    if (!term) return customers.slice(0, 60);

    const onlyNumbers = term.replace(/[^0-9]/g, '');
    const isNumeric = onlyNumbers.length > 0;
    if (isNumeric && term.length < 4) return [];
    if (!isNumeric && term.length < 2) return [];

    return customers.filter((customer) => {
      const company = (customer.company_name || '').toLowerCase();
      const manager = (customer.manager || '').toLowerCase();
      const phone = (customer.phone || '').toLowerCase();
      const mobile = (customer.mobile || '').toLowerCase();
      const address = (customer.address || '').toLowerCase();
      
      return company.includes(term) || 
             manager.includes(term) || 
             phone.includes(term) || 
             mobile.includes(term) ||
             address.includes(term);
    });
  }, [customers, customerSearchTerm]);

  const handleDeleteRecord = async (id) => {
    if (!window.confirm('이 작업을 삭제하시겠습니까?')) return;

    try {
      const response = await fetch(`${API_URL}/records/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        alert('삭제되었습니다.');
        fetchRecords();
      } else {
        alert('삭제 실패');
      }
    } catch (err) {
      alert('삭제 중 오류 발생');
    }
  };

  const handleDeleteCustomer = async (id) => {
    if (!window.confirm('이 고객 정보를 삭제하시겠습니까?')) return;

    try {
      const response = await fetch(`${API_URL}/customers/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        alert('삭제되었습니다.');
        fetchCustomers();
      } else {
        alert('삭제 실패');
      }
    } catch (err) {
      alert('삭제 중 오류 발생');
    }
  };

  // CSV 파일 불러오기 (복구)
  const handleRestoreCSV = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      alert('CSV 파일만 업로드 가능합니다.');
      return;
    }

    if (!window.confirm('⚠️ CSV 파일을 불러오시겠습니까?\n\n기존 데이터는 유지되며, CSV의 데이터가 추가/업데이트됩니다.')) {
      event.target.value = '';
      return;
    }

    setIsRestoring(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/import/csv`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        alert(`✅ 복구 완료!\n\n추가: ${result.added || 0}건\n업데이트: ${result.updated || 0}건\n실패: ${result.failed || 0}건`);
        fetchRecords();
        fetchCustomers();
      } else {
        const error = await response.json();
        alert(`복구 실패: ${error.error || '알 수 없는 오류'}`);
      }
    } catch (err) {
      console.error('CSV 복구 오류:', err);
      alert('CSV 복구 중 오류가 발생했습니다.');
    } finally {
      setIsRestoring(false);
      event.target.value = '';
    }
  };

  // DB 초기화
  const handleInitializeDB = async () => {
    const confirmMsg = '⚠️⚠️⚠️ 경고 ⚠️⚠️⚠️\n\n데이터베이스를 초기화하면 모든 데이터가 삭제됩니다!\n\n작업 데이터와 고객 정보가 모두 삭제되며 복구할 수 없습니다.\n\n정말로 초기화하시겠습니까?';
    
    if (!window.confirm(confirmMsg)) return;

    const doubleConfirm = window.prompt('정말로 초기화하려면 "초기화"를 입력하세요:');
    if (doubleConfirm !== '초기화') {
      alert('초기화가 취소되었습니다.');
      return;
    }

    setIsInitializing(true);

    try {
      const response = await fetch(`${API_URL}/db/initialize`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        alert('✅ 데이터베이스가 초기화되었습니다.');
        fetchRecords();
        fetchCustomers();
      } else {
        const error = await response.json();
        alert(`초기화 실패: ${error.error || '알 수 없는 오류'}`);
      }
    } catch (err) {
      console.error('DB 초기화 오류:', err);
      alert('데이터베이스 초기화 중 오류가 발생했습니다.');
    } finally {
      setIsInitializing(false);
    }
  };

  // SQL 파일 불러오기
  const handleLoadSQL = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.sql')) {
      alert('SQL 파일만 업로드 가능합니다.');
      return;
    }

    if (!window.confirm('⚠️ SQL 파일을 실행하시겠습니까?\n\nSQL 파일의 내용에 따라 데이터가 추가/수정/삭제될 수 있습니다.')) {
      event.target.value = '';
      return;
    }

    setIsLoadingSQL(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/db/load-sql`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        alert(`✅ SQL 파일 실행 완료!\n\n실행된 명령: ${result.executed || 0}개`);
        fetchRecords();
        fetchCustomers();
      } else {
        const error = await response.json();
        alert(`SQL 실행 실패: ${error.error || '알 수 없는 오류'}`);
      }
    } catch (err) {
      console.error('SQL 실행 오류:', err);
      alert('SQL 파일 실행 중 오류가 발생했습니다.');
    } finally {
      setIsLoadingSQL(false);
      event.target.value = '';
    }
  };

  return (
    <div style={formStyles.dashboardWrapper}>
      {/* 좌측: 고객 정보 관리 폼 */}
      <CustomerManagementForm token={token} onUpdate={fetchCustomers} />

      {/* 우측: 탭 형태 */}
      <section style={tableStyles.listContainer}>
        {/* 탭 헤더 + 버튼들 */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          borderBottom: '2px solid #ecf0f1',
          marginBottom: '20px'
        }}>
          <div style={{ display: 'flex' }}>
            <button
              onClick={() => setActiveTab('records')}
              style={{
                padding: '12px 24px',
                fontSize: '16px',
                fontWeight: 'bold',
                background: 'none',
                border: 'none',
                borderBottom: activeTab === 'records' ? '3px solid #3498db' : 'none',
                color: activeTab === 'records' ? '#3498db' : '#7f8c8d',
                cursor: 'pointer'
              }}
            >
              작업 데이터 관리
            </button>
            <button
              onClick={() => setActiveTab('customers')}
              style={{
                padding: '12px 24px',
                fontSize: '16px',
                fontWeight: 'bold',
                background: 'none',
                border: 'none',
                borderBottom: activeTab === 'customers' ? '3px solid #3498db' : 'none',
                color: activeTab === 'customers' ? '#3498db' : '#7f8c8d',
                cursor: 'pointer',
                marginLeft: '10px'
              }}
            >
              고객 정보 관리
            </button>
          </div>
          
          {/* 버튼 그룹 */}
          <div style={{ display: 'flex', gap: '10px', marginBottom: '8px', flexWrap: 'wrap' }}>
            {/* CSV 내보내기 */}
            <a 
              href={`${API_URL}/export/csv`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                padding: '8px 16px',
                backgroundColor: '#27ae60',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'inline-block'
              }}
              onClick={(e) => {
                e.preventDefault();
                fetch(`${API_URL}/export/csv`, {
                  headers: { 'Authorization': `Bearer ${token}` }
                })
                .then(response => response.blob())
                .then(blob => {
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `작업목록_${new Date().toISOString().split('T')[0]}.csv`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  window.URL.revokeObjectURL(url);
                })
                .catch(err => alert('CSV 내보내기 실패'));
              }}
            >
              📥 CSV 내보내기
            </a>
			 {/* SQL 내보내기 (전체 백업) */}
<button
  onClick={() => {
    fetch(`${API_URL}/export/sql`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
    .then(response => response.blob())
    .then(blob => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pctools_backup_${new Date().toISOString().split('T')[0]}.sql`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    })
    .catch(err => alert('SQL 내보내기 실패'));
  }}
  style={{
    padding: '8px 16px',
    backgroundColor: '#2980b9',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer'
  }}
>
  💾 SQL 내보내기
</button>

            {/* CSV 불러오기 */}
            <label style={{
              padding: '8px 16px',
              backgroundColor: isRestoring ? '#95a5a6' : '#e67e22',
              color: 'white',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: isRestoring ? 'not-allowed' : 'pointer',
              display: 'inline-block'
            }}>
              {isRestoring ? '⏳ 복구 중...' : '📤 CSV 불러오기'}
              <input 
                type="file" 
                accept=".csv"
                onChange={handleRestoreCSV}
                disabled={isRestoring}
                style={{ display: 'none' }}
              />
            </label>

            {/* SQL 불러오기 */}
            <label style={{
              padding: '8px 16px',
              backgroundColor: isLoadingSQL ? '#95a5a6' : '#9b59b6',
              color: 'white',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: isLoadingSQL ? 'not-allowed' : 'pointer',
              display: 'inline-block'
            }}>
              {isLoadingSQL ? '⏳ 실행 중...' : '💾 SQL 불러오기'}
              <input 
                type="file" 
                accept=".sql"
                onChange={handleLoadSQL}
                disabled={isLoadingSQL}
                style={{ display: 'none' }}
              />
            </label>

            {/* DB 초기화 */}
            <button
              onClick={handleInitializeDB}
              disabled={isInitializing}
              style={{
                padding: '8px 16px',
                backgroundColor: isInitializing ? '#95a5a6' : '#c0392b',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: 'bold',
                cursor: isInitializing ? 'not-allowed' : 'pointer'
              }}
            >
              {isInitializing ? '⏳ 초기화 중...' : '🗑️ DB 초기화'}
            </button>
          </div>
        </div>

        {/* 작업 데이터 관리 탭 */}
        {activeTab === 'records' && (
          <>
            <div style={{ marginBottom: '15px' }}>
              <input 
                type="text" 
                placeholder="업체명, 담당자, 전화번호, 접수내역 검색..." 
                value={recordSearchTerm} 
                onChange={(e) => setRecordSearchTerm(e.target.value)} 
                style={{
                  ...tableStyles.searchInput,
                  width: '100%',
                  padding: '10px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={tableStyles.statsBar}>
              전체: <strong>{records.length}</strong>건
              <span style={{ marginLeft: '20px' }}>표시: <strong>{filteredRecords.length}</strong>건</span>
            </div>

            <div style={tableStyles.tableWrapper}>
              <table style={tableStyles.responsiveTable}>
                <thead>
                  <tr>
                    <th style={{ ...tableStyles.tableHeader, width: '100px' }}>접수일</th>
                    <th style={{ ...tableStyles.tableHeader, width: '150px' }}>업체명</th>
                    <th style={{ ...tableStyles.tableHeader, width: '100px' }}>담당자</th>
                    <th style={{ ...tableStyles.tableHeader, width: '120px' }}>전화번호</th>
                    <th style={tableStyles.tableHeader}>접수내역</th>
                    <th style={tableStyles.tableHeader}>처리내역</th>
                    <th style={{ ...tableStyles.tableHeader, width: '100px', textAlign: 'right' }}>전체비용</th>
                    <th style={{ ...tableStyles.tableHeader, width: '80px', textAlign: 'center' }}>진행상태</th>
                    <th style={{ ...tableStyles.tableHeader, width: '60px', textAlign: 'center' }}>삭제</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRecords.map((record) => (
                    <tr key={record.id}>
                      <td style={tableStyles.tableCell}>{formatDate(record.receipt_date)}</td>
                      <td style={{ ...tableStyles.tableCell, fontWeight: 'bold' }}>{record.company_name}</td>
                      <td style={tableStyles.tableCell}>{record.manager || '-'}</td>
                      <td style={tableStyles.tableCell}>{record.phone || record.mobile || '-'}</td>
                      <td style={{ ...tableStyles.tableCell, ...tableStyles.ellipsis, maxWidth: '200px' }}>{record.receipt_details}</td>
                      <td style={{ ...tableStyles.tableCell, ...tableStyles.ellipsis, maxWidth: '200px' }}>{record.process_details || '-'}</td>
                      <td style={{ ...tableStyles.tableCell, textAlign: 'right' }}>{record.total_cost ? formatMoney(record.total_cost) : '-'}</td>
                      <td style={{ ...tableStyles.tableCell, textAlign: 'center' }}>
                        <span style={tableStyles.statusBadge(record.progress_status)}>{record.progress_status}</span>
                      </td>
                      <td style={{ ...tableStyles.tableCell, textAlign: 'center' }}>
                        <button 
                          onClick={() => handleDeleteRecord(record.id)}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: '#e74c3c',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          삭제
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* 고객 정보 관리 탭 */}
        {activeTab === 'customers' && (
          <>
            <div style={{ marginBottom: '15px' }}>
              <input 
                type="text" 
                placeholder="업체명, 담당자, 전화번호, 주소 검색..." 
                value={customerSearchTerm} 
                onChange={(e) => setCustomerSearchTerm(e.target.value)} 
                style={{
                  ...tableStyles.searchInput,
                  width: '100%',
                  padding: '10px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={tableStyles.statsBar}>
              전체: <strong>{customers.length}</strong>건
              <span style={{ marginLeft: '20px' }}>표시: <strong>{filteredCustomers.length}</strong>건</span>
            </div>

            <div style={tableStyles.tableWrapper}>
              <table style={tableStyles.responsiveTable}>
                <thead>
                  <tr>
                    <th style={{ ...tableStyles.tableHeader, width: '80px' }}>ID</th>
                    <th style={{ ...tableStyles.tableHeader, width: '200px' }}>업체명</th>
                    <th style={{ ...tableStyles.tableHeader, width: '120px' }}>담당자</th>
                    <th style={{ ...tableStyles.tableHeader, width: '140px' }}>전화번호</th>
                    <th style={tableStyles.tableHeader}>주소</th>
                    <th style={{ ...tableStyles.tableHeader, width: '60px', textAlign: 'center' }}>삭제</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCustomers.map((customer) => (
                    <tr key={customer.id}>
                      <td style={tableStyles.tableCell}>{customer.id}</td>
                      <td style={{ ...tableStyles.tableCell, fontWeight: 'bold' }}>{customer.company_name}</td>
                      <td style={tableStyles.tableCell}>{customer.manager || '-'}</td>
                      <td style={tableStyles.tableCell}>
                        {customer.phone && customer.mobile 
                          ? `${customer.phone} / ${customer.mobile}`
                          : customer.phone || customer.mobile || '-'}
                      </td>
                      <td style={{ ...tableStyles.tableCell, ...tableStyles.ellipsis, maxWidth: '300px' }}>{customer.address || '-'}</td>
                      <td style={{ ...tableStyles.tableCell, textAlign: 'center' }}>
                        <button 
                          onClick={() => handleDeleteCustomer(customer.id)}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: '#e74c3c',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          삭제
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>
    </div>
  );
}

export default DBManagement;
